<template>
    <div>
        <breadcrumbs class="mb-3" />
        <file-download-modal
            v-model="isShowDownloadExcelMenu"
            :radio-items="useRadioItemsDownloadExcel"
            :label="$t('online_payments')"
            :loading="isLoadingDownloadExcel"
            class="mb-2"
            @change-radio-group="radioGroupDownloadExcel = $event"
            @download="downloadExcel"
        />
        <OnlinePaymentsFilter
            v-model="filters"
            :online-payments-providers="onlinePaymentsProviders"
            :has-active-filters="hasActiveFilters"
            @submit="loadData()"
            @reset="resetFilters"
        />
        <v-data-table-server
            :headers="useHeaders"
            :items="onlinePaymentTransactions"
            :items-per-page-options="useTableOptions(totalCount)"
            :options="tableOptions"
            :loading="isLoading"
            :items-length="totalCount"
            density="compact"
            class="elevation-1 table-server"
            @update:options="onUpdateTableOptions"
        >
            <template v-slot:item.reservationNumber="{item}">
                <nuxt-link
                    no-prefetch
                    :to="{name: 'order-details', params: {id: +item.reservationNumber}}"
                    @click.native.stop
                >
                    {{ +item.reservationNumber }}
                </nuxt-link>
            </template>
            <template v-slot:item.paymentStatus="{item}">
                <span class="text-subtitle-2 font-weight-medium">{{ item.paymentStatus }}</span>
            </template>
        </v-data-table-server>
    </div>
</template>

<script setup lang="ts">
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import {downloadData, isEqual} from '~/utils/helpers'
    import FileDownloadModal from '~/components/account/snippets/FileDownloadModal.vue'
    import {useTableOptions} from '~/composables/helpers'

    const initOnlinePaymentsFilter = () => ({
        paymentDateFrom: null,
        paymentDateTo: null,
        referenceNumber: null,
        paymentNumber: null,
        paymentAccount: null,
        paymentProviderCode: null,
    })

    const isLoading = ref<boolean>(false)
    const isShowDownloadExcelMenu = ref<boolean>(false)
    const isLoadingDownloadExcel = ref<boolean>(false)
    const filters = ref(initOnlinePaymentsFilter())
    const tableOptions = ref({
        itemsPerPage: 10,
        page: 1,
    })
    const onlinePaymentTransactions = ref<unknown[]>([])
    const onlinePaymentsProviders = ref<unknown[]>([])
    const totalCount = ref<number>(0)
    const filterKey = ref<string>(null)
    const radioGroupDownloadExcel = ref<string>('all')

    const {$api, $toast} = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadOnlinePaymentsProviders()
        EventBus.$on(CHANGE_LOCALE_EVENT, loadData)
    })

    onBeforeUnmount(async () => {
        EventBus.$off(CHANGE_LOCALE_EVENT, loadData)
    })

    async function loadOnlinePaymentsProviders(): Promise<void> {
        try {
            onlinePaymentsProviders.value = await $api.onlinePaymentsProviders.get()
        } catch (e) {
            $toast.error(t('error_message.load_error'))
        }
    }

    async function loadData(): Promise<void> {
        const {itemsPerPage, page} = tableOptions.value
        try {
            isLoading.value = true
            const rq = {
                ...filters.value,
                limit: itemsPerPage,
                offset: itemsPerPage * (page - 1),
            }
            clean(rq)

            const res = await $api.onlinePayments.get(rq)
            filterKey.value = res.filterKey
            totalCount.value = res.totalCount
            onlinePaymentTransactions.value = res.onlinePaymentTransactions
        } catch (e) {
            $toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    const useHeaders = computed(() => {
        return [
            {title: t('filterTitles.supplierName'), value: 'supplierCompanyName'},
            {title: t('client'), value: 'clientCompanyName'},
            {title: t('online_payment_provider'), value: 'paymentProvider'},
            {title: t('transaction_number'), value: 'paymentNumber'},
            {title: t('payment_status'), value: 'paymentStatus'},
            {title: t('payment_date'), value: 'paymentDate'},
            {title: t('order_number'), value: 'reservationNumber'},
        ]
    })

    const useRadioItemsDownloadExcel = computed(() => {
        return [
            {
                value: 'filtered',
                label: `${t('filtered_payments')} (${totalCount.value})`,
            },
            {value: 'all', label: t('all_payments')},
        ]
    })

    const hasActiveFilters = computed(() => {
        return !isEqual(initOnlinePaymentsFilter(), filters.value)
    })

    const resetFilters = (): void => {
        filters.value = initOnlinePaymentsFilter()
        loadData()
    }

    const downloadExcel = async () => {
        const rq =
            radioGroupDownloadExcel.value === 'filtered'
                ? {
                      filterKey: filterKey.value,
                      orderType: authStore.orderType,
                  }
                : {orderType: authStore.orderType}
        try {
            isLoadingDownloadExcel.value = true
            const data = await $api.onlinePaymentsReport.get(rq)

            await downloadData(data, 'onlinePaymentTransactions.xlsx')
            isShowDownloadExcelMenu.value = false
        } catch (e) {
            $toast.error(e.msg || e.errors[0].message || t('error_message.something_went_wrong'))
        } finally {
            isLoadingDownloadExcel.value = false
        }
    }

    const onUpdateTableOptions = (options): void => {
        tableOptions.value = options
        loadData()
    }
</script>
